import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Building2, User, Shield, ChevronRight } from 'lucide-react'
import { PageHeader, useAuthStore, fadeUp } from '@personic/shared'
import { buildBreadcrumbs } from '@/config/routes'

export default function Organization() {
  const { user } = useAuthStore()

  const breadcrumbs = buildBreadcrumbs('/settings/account/organization')

  return (
    <div>
      <PageHeader
        title="Organization Profile"
        description="Manage your organization details and account information."
        breadcrumbs={breadcrumbs}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl">
        {/* Organization Info */}
        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="rounded-xl border border-warm-200 bg-white p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-copper-50 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-copper-500" />
            </div>
            <h2 className="font-heading font-semibold text-midnight-800">Organization</h2>
          </div>
          <dl className="space-y-3">
            <div>
              <dt className="text-xs text-warm-500 uppercase tracking-wide">Tenant</dt>
              <dd className="text-sm font-medium text-midnight-800">{user?.tenant?.name || 'N/A'}</dd>
            </div>
            <div>
              <dt className="text-xs text-warm-500 uppercase tracking-wide">Role</dt>
              <dd className="text-sm font-medium text-midnight-800 capitalize">{user?.role?.replace('_', ' ')}</dd>
            </div>
          </dl>
        </motion.div>

        {/* User Profile */}
        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="rounded-xl border border-warm-200 bg-white p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-copper-50 flex items-center justify-center">
              <User className="w-5 h-5 text-copper-500" />
            </div>
            <h2 className="font-heading font-semibold text-midnight-800">Your Profile</h2>
          </div>
          <dl className="space-y-3">
            <div>
              <dt className="text-xs text-warm-500 uppercase tracking-wide">Name</dt>
              <dd className="text-sm font-medium text-midnight-800">{user?.name}</dd>
            </div>
            <div>
              <dt className="text-xs text-warm-500 uppercase tracking-wide">Email</dt>
              <dd className="text-sm font-medium text-midnight-800">{user?.email}</dd>
            </div>
            <div>
              <dt className="text-xs text-warm-500 uppercase tracking-wide">MFA</dt>
              <dd className="text-sm font-medium text-midnight-800">
                {user?.mfa_enabled ? (
                  <span className="inline-flex items-center gap-1 text-sage-600">
                    <Shield className="w-3.5 h-3.5" /> Enabled
                  </span>
                ) : (
                  <span className="text-warm-500">Not enabled</span>
                )}
              </dd>
            </div>
          </dl>
        </motion.div>

        {/* Quick Links */}
        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="rounded-xl border border-warm-200 bg-white p-6 lg:col-span-2">
          <h2 className="font-heading font-semibold text-midnight-800 mb-4">Quick Links</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { label: 'Subscription & Plan', href: '/settings/account/subscription', desc: 'View or change your plan' },
              { label: 'Billing & Invoices', href: '/settings/account/billing', desc: 'Payment history and invoices' },
              { label: 'Usage Dashboard', href: '/settings/account/usage', desc: 'Monitor API and profile usage' },
            ].map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="flex items-center justify-between p-4 rounded-lg border border-warm-200 hover:border-copper-300 hover:bg-copper-50/50 transition-colors group"
              >
                <div>
                  <p className="text-sm font-medium text-midnight-800 group-hover:text-copper-700">{link.label}</p>
                  <p className="text-xs text-warm-500 mt-0.5">{link.desc}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-warm-300 group-hover:text-copper-500" />
              </Link>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  )
}
